import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    ButtonBase,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { SIDEBAR_LOGO } from '../../utils/constants';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import { useEffect, useState } from 'react';

const menuItems = [
    { icon: <HomeOutlinedIcon />, path: '/', label: 'Home', type: 'navigate' },
    {
        icon: <PersonOutlineOutlinedIcon />,
        path: '/users',
        label: 'Users',
        type: 'navigate',
        activePaths: ['/create-user', '/edit-user'],
    },
    { icon: <CalendarMonthOutlinedIcon />, path: '/terms', label: 'Terms', type: 'navigate' },
    { icon: <ListAltOutlinedIcon />, path: '/programs', label: 'Programs', type: 'navigate' },
    { icon: <MenuBookOutlinedIcon />, path: '/subjects', label: 'Subjects', type: 'navigate' },
    { icon: <VideoLibraryOutlinedIcon />, path: '/videos', label: 'Videos', type: 'navigate' },
    { icon: <LibraryBooksOutlinedIcon />, path: '/references', label: 'References' },
    { icon: <DescriptionOutlinedIcon />, path: '/references', label: 'Preboard' },
];

const Sidebar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [preboardOpen, setPreboardOpen] = useState(false);

    const isPreboardRoute = location.pathname.startsWith('/preboard');

    useEffect(() => {
        if (isPreboardRoute) {
            setPreboardOpen(true);
        }
    }, [isPreboardRoute]);

    const isActive = (item: any) => {
        const currentPath = location.pathname;
        return (
            currentPath === item.path ||
            (item.activePaths && item.activePaths.includes(currentPath))
        );
    };

    const handleNavigate = (label: string, path: string) => {
        navigate(path);
    };

    const togglePreboard = () => {
        setPreboardOpen(!preboardOpen);
    };

    return (
        <Box
            sx={{
                width: 270,
                minHeight: '100vh',
                background: 'linear-gradient(8.18deg, #010911 46.13%, #0B4176 141.32%)',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                px: 3,
                pt: 3,
                position: 'sticky',
                top: 0,
            }}
        >
            <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ alignSelf: 'center', mb: '40px', mt: '20px' }}
            >
                <Box component="img" src={SIDEBAR_LOGO} alt="Logo" sx={{ width: 150, height: 'auto' }} />
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {menuItems.map((item, index) => {
                    const selected = isActive(item);

                    // Handle Preboard dropdown separately
                    if (item.label === 'Preboard') {
                        return (
                            <Box key={index}>
                                <ButtonBase
                                    onClick={togglePreboard}
                                    sx={{
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        backgroundColor: preboardOpen ? '#FFD700' : 'transparent',
                                        color: preboardOpen ? 'black' : 'white',
                                        borderRadius: '8px',
                                        py: 1,
                                        px: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        '&:hover': {
                                            backgroundColor: preboardOpen ? '#FFD700' : 'rgba(255,255,255,0.1)',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {item.icon}
                                    </Box>
                                    <Typography variant="body1" component="span">
                                        {item.label}
                                    </Typography>
                                    <Box sx={{ marginLeft: 'auto' }}>
                                        {preboardOpen ? <ArrowDropUpOutlinedIcon /> : <ArrowDropDownOutlinedIcon />}
                                    </Box>
                                </ButtonBase>

                                {preboardOpen && (
                                    <Box sx={{ pl: 4, mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <ButtonBase
                                            onClick={() => handleNavigate('Examinee', '/preboard/examinees')}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                py: 1,
                                                px: 2,
                                                borderRadius: '8px',
                                                color: location.pathname === '/preboard/examinees' ? '#FFD700' : '#fff',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                                },
                                            }}
                                        >
                                            Examinees
                                        </ButtonBase>
                                        <ButtonBase
                                            onClick={() => handleNavigate('Exams', '/preboard/exams')}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                py: 1,
                                                px: 2,
                                                borderRadius: '8px',
                                                color: location.pathname === '/preboard/exams' ? '#FFD700' : '#fff',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                                },
                                            }}
                                        >
                                            Exams
                                        </ButtonBase>
                                    </Box>
                                )}
                            </Box>
                        );
                    }

                    return (
                        <ButtonBase
                            key={index}
                            onClick={() => handleNavigate(item.label, item.path)}
                            sx={{
                                width: '100%',
                                justifyContent: 'flex-start',
                                backgroundColor: selected ? '#FFD700' : 'transparent',
                                color: selected ? 'black' : 'white',
                                borderRadius: '8px',
                                py: 1,
                                px: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                '&:hover': {
                                    backgroundColor: selected ? '#FFD700' : 'rgba(255,255,255,0.1)',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                            <Typography variant="body1">{item.label}</Typography>
                        </ButtonBase>
                    );
                })}
            </Box>
        </Box>
    );
};

export default Sidebar;
